import { client } from '@/api/orpc'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useCases = defineStore('cases', () => {
	const list = ref<
		{
			type: 'regular'
			uuid: string
			name: string
			price: number
			risk: number
			version: number
		}[]
	>([])
	const rewards = ref<
		{
			type: 'reward'
			uuid: string
			name: string
			isLocked: boolean
			wonItem: {
				modelUuid: string
				preloader: string | null
				modelName: string
				collectionName: string
				price: number
				chance: number
			} | null
			expiresAt: number | null
		}[]
	>([])
	const rewardsLockedUntil = ref<Date>()
	const rewardCasesCount = ref(0)

	const sortExpensive = ref(true)

	const toggleSort = () => {
		sortExpensive.value = !sortExpensive.value
	}

	const search = ref('')

	const sorted = computed(() => {
		const lowered = search.value.toLowerCase()

		return list.value
			.filter((item) => {
				if (selectedTag.value === 0) return true // All cases
				if (selectedTag.value === 1 && item.risk < 33) return true // Low risk
				if (selectedTag.value === 2 && item.risk >= 33 && item.risk < 66)
					return true // Medium risk
				if (selectedTag.value === 3 && item.risk >= 66) return true // High risk
				return false
			})
			.filter((item) => item.name.toLowerCase().includes(lowered))
			.sort((a, b) => {
				return sortExpensive.value ? b.price - a.price : a.price - b.price
			})
	})

	const selectedTag = ref(0)

	const loading = ref(false)

	const getCases = async () => {
		loading.value = true

		const { error, data } = await client.game.getCases({
			type: 'regular'
		})

		loading.value = false

		if (error) {
			console.error(error)
			return
		}

		if (!data) {
			return
		}

		list.value = data.filter((item) => item.type === 'regular')
	}

	getCases()

	const rewardsLoading = ref(true)
	let timeout: number | undefined
	const lockedUntil = ref('')
	setInterval(() => {
		calcLockedUntil()
	}, 1000)

	const formatLessThanTen = (num: number): string => {
		return num < 10 ? '0' + num : num.toString()
	}

	const calcLockedUntil = () => {
		if (!rewardsLockedUntil.value) {
			lockedUntil.value = ''
			return
		}

		const totalSeconds = Math.floor(
			(rewardsLockedUntil.value.getTime() - Date.now()) / 1000
		)

		if (totalSeconds <= 0) {
			lockedUntil.value = '00:00:00'
			return
		}

		const hours = Math.floor(totalSeconds / 3600)
		const minutes = Math.floor((totalSeconds % 3600) / 60)
		const seconds = totalSeconds % 60

		lockedUntil.value = `${formatLessThanTen(hours)}:${formatLessThanTen(minutes)}:${formatLessThanTen(seconds)}`
	}

	const getRewardCases = async () => {
		const { error, data } = await client.game.getCases({ type: 'reward' })

		rewardsLoading.value = false

		if (error) {
			console.error(error)
			return
		}

		if (!data) {
			return
		}

		rewards.value = data.filter((item) => item.type === 'reward')

		if (!rewards.value.length) {
			return
		}

		setRewardCasesCount(
			rewards.value.filter((item) => !item.isLocked && !item.wonItem).length
		)

		rewardsLockedUntil.value = new Date(
			rewards.value[0].expiresAt || Date.now()
		)

		calcLockedUntil()

		const timeUntilUnlock = rewardsLockedUntil.value.getTime() - Date.now()

		if (timeout) {
			clearTimeout(timeout)
		}

		if (!timeUntilUnlock || timeUntilUnlock <= 0) {
			return
		}

		timeout = setTimeout(() => {
			getRewardCases()
		}, timeUntilUnlock)
	}

	const setRegularCase = (uuid: string, price: number, version: number) => {
		const foundCase = list.value.find((c) => c.uuid === uuid)

		if (!foundCase) {
			return
		}

		foundCase.price = price
		foundCase.version = version
	}

	const setRewardCase = (
		uuid: string,
		wonItem: {
			chance: number
			price: number
			modelUuid: string
			preloader: string | null
			modelName: string
			collectionName: string
		}
	) => {
		const item = rewards.value.find((item) => item.uuid === uuid)

		if (!item) {
			return
		}

		item.wonItem = wonItem
	}

	const welcomeCase = ref<{
		uuid: string
		name: string
		count: number
	}>()

	const setWelcomeCase = (uuid: string, count: number, name: string) => {
		welcomeCase.value = { uuid, count, name }
	}

	const setWelcomeCaseCount = (count: number) => {
		if (!welcomeCase.value) {
			return
		}

		welcomeCase.value.count = count
	}

	const setRewardCasesCount = (count: number) => {
		rewardCasesCount.value = count
	}

	const referralCase = ref<{
		uuid: string
		canOpen: boolean
		requiredReferrals: number
		qualifiedReferrals: number
	}>()

	const setReferralCase = (
		uuid: string,
		canOpen: boolean,
		requiredReferrals: number,
		qualifiedReferrals: number
	) => {
		referralCase.value = {
			uuid,
			canOpen,
			requiredReferrals,
			qualifiedReferrals
		}
	}

	const setReferralOpen = (canOpen: boolean) => {
		if (!referralCase.value) {
			return
		}

		referralCase.value.canOpen = canOpen
	}

	return {
		list,
		sortExpensive,
		toggleSort,
		search,
		sorted,
		selectedTag,
		loading,
		rewards,
		rewardsLoading,
		getRewardCases,
		rewardsLockedUntil,
		setRewardCase,
		lockedUntil,
		welcomeCase,
		setWelcomeCase,
		setWelcomeCaseCount,
		rewardCasesCount,
		setRewardCasesCount,

		referralCase,
		setReferralCase,
		setReferralOpen,
		getCases,

		setRegularCase
	}
})
